import React, { useState } from "react";

import { Autocomplete, Button, Stack, TextField } from "@mui/material";
import { DesktopDatePicker } from "@mui/x-date-pickers";
import { ProgressOverlay } from "components/Progress-overlay/ProgressOverlay";
import dayjs from "dayjs";
import { useDateFormatSelect } from "store";
import toIsoMidday from "utils/to-iso-midday";

interface Props {
  onSubmit: (data: { date: string; provider: string; value: string }) => Promise<void>;
  onCancel: () => void;
}

interface State {
  date: null | dayjs.Dayjs;
  provider: { key: string; title: string } | null;
  value: string;
}

const defaultState: State = {
  date: null,
  provider: null,
  value: ""
};

const ratingOptions = [
  "Aaa / AAA",
  "Aa1 / AA+",
  "Aa2 / AA",
  "Aa3 / AA-",
  "A1 / A+",
  "A2 / A",
  "A3 / A-",
  "Baa1 / BBB+",
  "Baa2 / BBB",
  "Baa3 / BBB-",
  "Ba1 / BB+",
  "Ba2 / BB",
  "Ba3 / BB-",
  "B1 / B+",
  "B2 / B",
  "B3 / B-",
  "Caa1 / CCC+",
  "Caa2 / CCC",
  "Caa3 / CCC-",
  "Ca / CC"
];

const providerOptions = [
  { key: "internal", title: "Internal" },
  { key: "sp", title: "S&P" },
  { key: "moodys", title: "Moodys" },
  { key: "fitch", title: "Fitch" }
  // { key: "msci", title: "MSCI" }
];

export const Form: React.FC<Props> = ({ onSubmit, onCancel }) => {
  const dateFormat = useDateFormatSelect();

  const [state, setState] = useState<State>(defaultState);
  const [isLoading, setIsLoading] = useState(false);

  const isValid = state.date && state.provider && state.value;

  const handleSubmit = async () => {
    if (!isValid) return;
    try {
      setIsLoading(true);
      await onSubmit({
        date: toIsoMidday(state.date?.toISOString()!),
        provider: state.provider!.key,
        value: state.value
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <ProgressOverlay inProgress={isLoading}>
      <Stack sx={{ flexDirection: "row", alignItems: "center", gap: "10px" }}>
        <DesktopDatePicker
          sx={{
            flex: "25%",
            height: "32px",
            "& .MuiInputBase-root": {
              height: "32px",
              fontSize: "14px",
              lineHeight: "16px"
            }
          }}
          format={dateFormat}
          views={["year", "month", "day"]}
          value={state.date}
          label="Date"
          onChange={(date) => {
            if (date && date.isValid()) {
              setState({ ...state, date });
            }
          }}
        />
        <Autocomplete
          sx={{
            flex: "25%",
            "& .MuiInputBase-root": {
              height: "32px",
              fontSize: "14px",
              lineHeight: "16px"
            }
          }}
          options={providerOptions}
          getOptionLabel={(option) => option.title}
          renderOption={(props, option) => <li {...props}>{option.title}</li>}
          disableClearable
          renderInput={(params) => <TextField {...params} label="Provider" />}
          onChange={(e, provider) => setState({ ...state, provider })}
        />
        <Autocomplete
          sx={{
            flex: "25%",
            "& .MuiInputBase-root": {
              height: "32px",
              fontSize: "14px",
              lineHeight: "16px"
            }
          }}
          options={ratingOptions}
          disableClearable
          renderInput={(params) => <TextField {...params} label="Value" />}
          onChange={(e, value) => setState({ ...state, value })}
        />
        <Button variant="outlined" size="sm" onClick={onCancel}>
          Cancel
        </Button>
        <Button
          disabled={!isValid || isLoading}
          sx={{ whiteSpace: "nowrap" }}
          variant="contained"
          size="sm"
          onClick={handleSubmit}
        >
          Add Value
        </Button>
      </Stack>
    </ProgressOverlay>
  );
};
